import styles from "./ChatBox.module.css";
import Icon from "@lib/common/components/Icon/Icon";
import ChatAttachmentChips from "@lib/experiment/agent/ChatAttachmentChips";
import { notifications } from "@lib/notification/notification";
import type { PaperProps, TextareaProps } from "@mantine/core";
import { Button, Group, Paper, Stack, Textarea, Tooltip } from "@mantine/core";
import type { ChangeEvent, KeyboardEvent, Ref } from "react";
import { useCallback, useRef, useState } from "react";

// Mirror of MAX_DOC_BYTES, MAX_DOCS_PER_SESSION, and the _EXTENSION_TO_FORMAT
// allow-list in backend/sdlabs_agent/src/attachment.py. Surfaced client-side
// so the user gets immediate feedback instead of an `agent.error` round-trip.
// Keep in sync — the backend is authoritative.
const MAX_FILE_BYTES = 4_500_000;
const MAX_DOCS_PER_SESSION = 5;
const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx,.txt,.md";

export default function ChatBox({
  ref,
  onSubmit,
  onCancel,
  disabled = false,
  isStreaming = false,
  textareaProps = {},
  paperProps,
  enableAttachments = false,
  selectedFiles,
  onFilesChange,
  sessionAttachmentCount = 0
}: {
  ref: Ref<HTMLTextAreaElement>;
  onSubmit: (message: string, files: File[]) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  textareaProps?: TextareaProps;
  paperProps?: Partial<PaperProps>;
  enableAttachments?: boolean;
  // Optional controlled-mode props. When BOTH are provided, the parent owns
  // file state and is responsible for rendering the attachment chips wherever
  // it wants. When omitted, ChatBox manages files internally and renders the
  // chips inside the input Paper above the textarea.
  selectedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
  sessionAttachmentCount?: number;
}) {
  const [input, setInput] = useState("");
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isControlled = selectedFiles !== undefined && onFilesChange !== undefined;
  const files = isControlled ? selectedFiles : internalFiles;
  const setFiles = useCallback(
    (next: File[]) => {
      if (isControlled) {
        onFilesChange(next);
      } else {
        setInternalFiles(next);
      }
    },
    [isControlled, onFilesChange]
  );

  const remainingDocSlots = Math.max(0, MAX_DOCS_PER_SESSION - sessionAttachmentCount - files.length);

  const submit = useCallback(() => {
    const message = input.trim();
    if (message.length === 0) {
      return;
    }
    onSubmit(message, files);
    setInput("");
    if (!isControlled) {
      setInternalFiles([]);
    }
  }, [input, files, onSubmit, isControlled]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (isStreaming) {
          return;
        }
        submit();
      }
    },
    [isStreaming, submit]
  );

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const incoming = Array.from(e.target.files ?? []);
      // Reset the input so the same file can be re-picked after removal
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (incoming.length === 0) {
        return;
      }

      const oversized = incoming.filter(f => f.size > MAX_FILE_BYTES);
      if (oversized.length > 0) {
        notifications.warning(
          "File too large",
          `${oversized.map(f => f.name).join(", ")} exceeds the ${(MAX_FILE_BYTES / 1_000_000).toFixed(1)} MB per-file limit.`
        );
      }

      const sized = incoming.filter(f => f.size <= MAX_FILE_BYTES);
      const slotsLeft = MAX_DOCS_PER_SESSION - sessionAttachmentCount - files.length;
      const accepted = sized.slice(0, Math.max(0, slotsLeft));
      const dropped = sized.length - accepted.length;
      if (dropped > 0) {
        notifications.warning(
          "Document limit reached",
          `Only ${MAX_DOCS_PER_SESSION} documents are allowed per chat session. ${dropped} file(s) were not added.`
        );
      }

      if (accepted.length > 0) {
        setFiles([...files, ...accepted]);
      }
    },
    [files, sessionAttachmentCount, setFiles]
  );

  const removeFile = useCallback(
    (index: number) => {
      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      if (typeof onFilesChange === "function") {
        onFilesChange(updatedFiles);
      }
    },
    [files, setFiles, onFilesChange]
  );

  const submitDisabled = disabled || input.trim().length === 0;
  const canAttachMore = remainingDocSlots > 0 && !isStreaming;

  return (
    <Paper
      classNames={{ root: styles.textareaPaperWrapper }}
      shadow="xxs"
      px="sm"
      pt={5}
      pb="xs"
      withBorder
      w="100%"
      {...paperProps}
    >
      <Stack gap="xs" align="space-between" h="100%">
        <ChatAttachmentChips fileNames={files.map(f => f.name)} onRemove={removeFile} />
        <Textarea
          ref={ref}
          {...textareaProps}
          value={input}
          // Compose handlers to ensure both internal state and any external
          // onChange (e.g., typewriter animation control) are called
          onChange={e => {
            setInput(e.currentTarget.value);
            textareaProps.onChange?.(e);
          }}
          onKeyDown={handleKeyDown}
          size="md"
          autoFocus
          data-autofocus
          autosize
          variant="unstyled"
          minRows={2}
          radius="md"
          fw={500}
        />

        <Group justify="space-between" align="flex-end">
          <Group gap="xs">
            {enableAttachments && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <Tooltip
                  label={
                    canAttachMore
                      ? "Attach papers, datasheets, or any relevant document"
                      : `Document limit reached (${MAX_DOCS_PER_SESSION}/session)`
                  }
                >
                  <Button
                    color="gray"
                    variant="subtle"
                    size="sm"
                    miw="unset"
                    px="xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!canAttachMore}
                  >
                    <Icon name="paperclip" />
                  </Button>
                </Tooltip>
              </>
            )}
          </Group>
          {isStreaming ? (
            <Button color="gray" variant="light" size="xs" miw="unset" px="xs" onClick={onCancel}>
              <Icon name="stop" />
            </Button>
          ) : (
            <Button
              color="var(--mantine-primary-color-filled)"
              variant="filled"
              size="xs"
              miw="unset"
              px="xs"
              disabled={submitDisabled}
              onClick={submit}
            >
              <Icon name="arrow-up" />
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
